import { ReactElement, ReactNode } from 'react';
import { Button } from '@ff-it/ui';
import clsx from 'clsx';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import type { Instruction } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/list-item';
import type { RowOrGroup } from 'modules/campaign/plan/types';
import { useDraggable } from '../../useDragAndDrop';
import { cellControl, cellWrapper, depthVar, dragBtn, dropIndicator } from './style.css';
import { RowIcon } from './RowIcon';
import { RowActions } from './RowActions';
import { usePlanMutations } from 'modules/campaign/plan/state';

function DropIndicator({ instruction }: { instruction: Instruction }): ReactElement {
  return <div className={clsx(dropIndicator[instruction.operation], instruction.blocked && 'blocked')} />;
}

function CellControl({ row, children }: { row: RowOrGroup; children: ReactNode }): ReactElement {
  const { selectCell } = usePlanMutations();
  const editable = row._isGroup ? !row.config.locked : !row.config.supplier?.locked;

  return (
    <div
      className={clsx(cellControl, { editable })}
      data-testid="cell-control"
      onClick={(event) => {
        // HACK: for composite cell
        // if we click on title cell we are not eabling editor
        event.stopPropagation();
        // Clicks inside the portaled editor dialog (SupplierPicker) bubble here through the React
        // tree; re-enabling the editor from them would instantly reopen a just-closed editor
        if (!event.currentTarget.contains(event.target as Node)) {
          return;
        }
        selectCell(row._key, 'title', true, false);
      }}
    >
      {children}
    </div>
  );
}

type CellWrapperProps = { row: RowOrGroup; children: ReactNode };

export function CellWrapper({ row, children }: CellWrapperProps): ReactElement {
  const { handleRef, containerRef, isDragging, instruction } = useDraggable(row);

  return (
    <div
      className={clsx(cellWrapper, isDragging && 'dragging')}
      ref={containerRef}
      style={assignInlineVars({ [depthVar]: row.depth.toString() })}
    >
      {instruction && <DropIndicator instruction={instruction} />}
      <Button icon="grip-vertical" ref={handleRef} variant="ghost" testId="drag-handle" className={dragBtn} />
      <RowIcon row={row} />
      <CellControl row={row}>{children}</CellControl>
      <RowActions row={row} />
    </div>
  );
}
